import { Point } from 'highcharts';

import { EstimatedServiceLevelPerQueue, ForecastType, Queue } from '../../../graphql/components';
import { Values } from '../../../types';
import { createEndOfDateRange } from '../../../utils/dateRange';
import {
  AddElementPayload,
  AgentDaySettings,
  AutoScheduleOptions,
  AutoScheduleSelectedRange,
  ChartScheduleElement,
  ComposeDataPayload,
  DaySettings,
  MissingForecastPerQueue,
  ProductionChartScheduleElement,
  ProductionScheduleElementsCoverage,
  SchedulingAction,
  SchedulingChartType,
  TransformedProductionElementsWorkContribution,
  TransformedProductionForecast,
  TransformedStaffingPerQueue,
  TransformedWorkContribution,
} from '../types';

export type Action =
  | ReturnType<typeof addElement>
  | ReturnType<typeof calculateProductionElementsWorkContribution>
  | ReturnType<typeof restoreProductionElementsQueuesStaffing>
  | ReturnType<typeof calculateWorkContribution>
  | ReturnType<typeof clearSchedule>
  | ReturnType<typeof cloneSchedule>
  | ReturnType<typeof composeData>
  | ReturnType<typeof copySchedule>
  | ReturnType<typeof hideNonWorkingAgents>
  | ReturnType<typeof changeTimezone>
  | ReturnType<typeof changeTimezone>
  | ReturnType<typeof loadForecastData>
  | ReturnType<typeof loadForecastDataFailed>
  | ReturnType<typeof loadProductionElementsQueuesStaffing>
  | ReturnType<typeof loadProductionElementsQueuesStaffingFailed>
  | ReturnType<typeof loadProductionElementsWorkContribution>
  | ReturnType<typeof loadProductionElementsWorkContributionFailed>
  | ReturnType<typeof setLoadingProductionScheduleElements>
  | ReturnType<typeof loadWorkContribution>
  | ReturnType<typeof loadWorkContributionFailed>
  | ReturnType<typeof pasteSchedule>
  | ReturnType<typeof pinAllScheduleElements>
  | ReturnType<typeof pinElementsInCurrentDay>
  | ReturnType<typeof pinSelectedElements>
  | ReturnType<typeof reloadChartData>
  | ReturnType<typeof reloadScheduleAfterAutoScheduleFinished>
  | ReturnType<typeof removeAgents>
  | ReturnType<typeof removeElements>
  | ReturnType<typeof resetAutoScheduleOptionsAndSelectedRange>
  | ReturnType<typeof resetDirtyFlag>
  | ReturnType<typeof resolveConflictsOnElements>
  | ReturnType<typeof restoreForecastData>
  | ReturnType<typeof restoreProductionScheduleElements>
  | ReturnType<typeof rewindSchedule>
  | ReturnType<typeof selectAgents>
  | ReturnType<typeof selectAll>
  | ReturnType<typeof setAutoScheduleOptions>
  | ReturnType<typeof setAutoScheduleSelectedRange>
  | ReturnType<typeof setLockedAgentsIds>
  | ReturnType<typeof showAllAgents>
  | ReturnType<typeof toggleSelectScheduleElementsState>
  | ReturnType<typeof unpinAllScheduleElements>
  | ReturnType<typeof unpinElementsInCurrentDay>
  | ReturnType<typeof unpinSelectedElements>
  | ReturnType<typeof updateAgentDaySetting>
  | ReturnType<typeof updateDate>
  | ReturnType<typeof updateDaySetting>
  | ReturnType<typeof updateElements>
  | ReturnType<typeof changeSourceOfForecast>
  | ReturnType<typeof loadEstimatedServiceLevel>
  | ReturnType<typeof loadEstimatedServiceLevelFailed>
  | ReturnType<typeof calculateEstimatedServiceLevel>;

export const addElement = (element: AddElementPayload) => ({
  payload: element,
  type: SchedulingAction.ADD_ELEMENT,
});

export const composeData = (payload: ComposeDataPayload) => ({
  payload,
  type: SchedulingAction.COMPOSE_DATA,
});

export const removeAgents = (removedAgentsIds: string[]) => ({
  payload: { removedAgentsIds },
  type: SchedulingAction.REMOVE_AGENTS,
});

export const removeElements = () => ({
  type: SchedulingAction.REMOVE_ELEMENTS,
});

export const selectAgents = (agentsIds: string[]) => ({
  payload: agentsIds,
  type: SchedulingAction.SELECT_AGENTS,
});

export const updateDate = (startSchedule: number) => ({
  payload: {
    startSchedule,
    endSchedule: createEndOfDateRange(startSchedule).getTime(),
  },
  type: SchedulingAction.UPDATE_DATE,
});

export const updateElements = (updatedPoints: Array<Point & ChartScheduleElement>, points: ChartScheduleElement[]) => ({
  updatedPoints,
  points,
  type: SchedulingAction.UPDATE_ELEMENTS,
});

export const copySchedule = (agentId: string) => ({
  payload: { agentId },
  type: SchedulingAction.COPY_SCHEDULE,
});

export const cloneSchedule = (agentIds: string[], dates: Date[]) => ({
  payload: { agentIds, dates },
  type: SchedulingAction.CLONE_SCHEDULE,
});

export const pasteSchedule = (agentId: string, y: number) => ({
  payload: { agentId, y },
  type: SchedulingAction.PASTE_SCHEDULE,
});

export const clearSchedule = (agentId: string) => ({
  payload: { agentId },
  type: SchedulingAction.CLEAR_SCHEDULE,
});

export const selectAll = (agentId: string) => ({
  payload: { agentId },
  type: SchedulingAction.SELECT_ALL_AGENT_SCHEDULE,
});

export const updateDaySetting = <T extends keyof DaySettings>(dayIndex: number, setting: T, value: DaySettings[T]) => ({
  type: SchedulingAction.UPDATE_DAY_SETTING,
  payload: {
    dayIndex,
    setting,
    value,
  },
});

export const updateAgentDaySetting = <T extends keyof AgentDaySettings>(
  agentId: string,
  dayIndex: number,
  setting: T,
  value: AgentDaySettings[T]
) => ({
  type: SchedulingAction.UPDATE_AGENT_DAY_SETTING,
  payload: {
    agentId,
    dayIndex,
    setting,
    value,
  },
});

export const showAllAgents = () => ({
  type: SchedulingAction.SHOW_ALL_AGENTS,
});

export const hideNonWorkingAgents = () => ({
  type: SchedulingAction.HIDE_NON_WORKING_AGENTS,
});

export const rewindSchedule = (statePosition: number) => ({
  payload: { statePosition },
  type: SchedulingAction.REWIND_SCHEDULE,
});

export const resetDirtyFlag = () => ({
  type: SchedulingAction.RESET_DIRTY_FLAG,
});

export const loadWorkContribution = () => ({
  type: SchedulingAction.LOAD_WORK_CONTRIBUTION,
});

export const loadWorkContributionFailed = () => ({
  type: SchedulingAction.LOAD_WORK_CONTRIBUTION_FAILED,
});

export const calculateWorkContribution = (workContributionInputData: TransformedWorkContribution) => ({
  payload: { workContributionInputData },
  type: SchedulingAction.CALCULATE_WORK_CONTRIBUTION,
});

export const loadForecastData = () => ({
  type: SchedulingAction.LOAD_FORECAST_DATA,
});

export const loadForecastDataFailed = () => ({
  type: SchedulingAction.LOAD_FORECAST_DATA_FAILED,
});

export const restoreForecastData = (
  forecast: TransformedProductionForecast,
  missingOrIncompleteForecastQueueIds: string[],
  missingForecastIntervalsPerQueue: MissingForecastPerQueue
) => ({
  payload: {
    forecast,
    missingOrIncompleteForecastQueueIds,
    missingForecastIntervalsPerQueue,
  },
  type: SchedulingAction.RESTORE_FORECAST_DATA,
});

export const setLockedAgentsIds = (lockedAgentsIds: Record<string, boolean>) => ({
  payload: { lockedAgentsIds },
  type: SchedulingAction.SET_LOCKED_AGENTS_IDS,
});

export const changeTimezone = (timezone: string, minDate: number, maxDate: number) => ({
  payload: {
    maxDate,
    minDate,
    timezone,
  },
  type: SchedulingAction.CHANGE_TIMEZONE,
});

export const reloadScheduleAfterAutoScheduleFinished = (
  scheduleElements: ChartScheduleElement[],
  scheduleElementsForGantt: ChartScheduleElement[],
  newInitialQueuesStaffing?: TransformedStaffingPerQueue
) => ({
  payload: { scheduleElements, scheduleElementsForGantt, newInitialQueuesStaffing },
  type: SchedulingAction.RELOAD_SCHEDULE_AFTER_AUTO_SCHEDULE_FINISHED,
});

export const toggleSelectScheduleElementsState = (
  scheduleElementIds: string[],
  preserveOtherElementsState: boolean
) => ({
  payload: {
    preserveOtherElementsState,
    scheduleElementIds,
  },
  type: SchedulingAction.TOGGLE_SELECT_SCHEDULE_ELEMENTS_STATE,
});

export const pinAllScheduleElements = (agentId?: string) => ({
  payload: {
    agentId,
  },
  type: SchedulingAction.PIN_ALL_SCHEDULE_ELEMENTS,
});

export const pinElementsInCurrentDay = (agentId?: string) => ({
  payload: {
    agentId,
  },
  type: SchedulingAction.PIN_ELEMENTS_IN_CURRENT_DAY,
});

export const pinSelectedElements = (agentId?: string) => ({
  payload: {
    agentId,
  },
  type: SchedulingAction.PIN_SELECTED_ELEMENTS,
});

export const unpinAllScheduleElements = (agentId?: string) => ({
  payload: {
    agentId,
  },
  type: SchedulingAction.UNPIN_ALL_SCHEDULE_ELEMENTS,
});

export const unpinElementsInCurrentDay = (agentId?: string) => ({
  payload: {
    agentId,
  },
  type: SchedulingAction.UNPIN_ELEMENTS_IN_CURRENT_DAY,
});

export const unpinSelectedElements = (agentId?: string) => ({
  payload: {
    agentId,
  },
  type: SchedulingAction.UNPIN_SELECTED_ELEMENTS,
});

export const setAutoScheduleOptions = (optionKey: keyof AutoScheduleOptions, optionValue: number) => ({
  payload: { optionKey, optionValue },
  type: SchedulingAction.SET_AUTO_SCHEDULE_OPTIONS,
});

export const setAutoScheduleSelectedRange = <T extends AutoScheduleSelectedRange, K extends keyof T>(
  rangeKey: K,
  value: T[K]
) => ({
  payload: { rangeKey, value },
  type: SchedulingAction.SET_AUTO_SCHEDULE_SELECTED_RANGE,
});

export const resetAutoScheduleOptionsAndSelectedRange = (endDateTime: number, startDateTime: number) => ({
  payload: { endDateTime, startDateTime },
  type: SchedulingAction.RESET_AUTO_SCHEDULE_OPTIONS_AND_SELECTED_RANGE,
});

export const reloadChartData = (agentIds: string[]) => ({
  payload: {
    agentIds,
  },
  type: SchedulingAction.RELOAD_CHART_DATA,
});

export const setLoadingProductionScheduleElements = (newStatus: boolean) => ({
  payload: {
    isLoadingProductionScheduleElements: newStatus,
  },
  type: SchedulingAction.SET_LOADING_PRODUCTION_SCHEDULE_ELEMENTS,
});

export const restoreProductionScheduleElements = (
  productionScheduleElements: ProductionChartScheduleElement[],
  productionScheduleElementsCoverage: ProductionScheduleElementsCoverage[],
  productionScheduleElementsMissingQueueIds: string[],
  missingQueuesData: Record<string, Queue>
) => ({
  payload: {
    missingQueuesData,
    productionScheduleElements,
    productionScheduleElementsCoverage,
    productionScheduleElementsMissingQueueIds,
  },
  type: SchedulingAction.RESTORE_PRODUCTION_SCHEDULE_ELEMENTS,
});

export const resolveConflictsOnElements = (productionElementsInConflict: ProductionChartScheduleElement[]) => ({
  payload: {
    productionElementsInConflict,
  },
  type: SchedulingAction.RESOLVE_CONFLICTS_ON_ELEMENTS,
});

export const loadProductionElementsQueuesStaffing = () => ({
  type: SchedulingAction.LOAD_PRODUCTION_ELEMENTS_QUEUES_STAFFING,
});

export const loadProductionElementsQueuesStaffingFailed = () => ({
  type: SchedulingAction.LOAD_PRODUCTION_ELEMENTS_QUEUES_STAFFING_FAILED,
});

export const restoreProductionElementsQueuesStaffing = (
  productionElementsQueuesStaffing: TransformedStaffingPerQueue
) => ({
  payload: {
    productionElementsQueuesStaffing,
  },
  type: SchedulingAction.RESTORE_PRODUCTION_ELEMENTS_QUEUES_STAFFING,
});

export const changeSourceOfForecast = (
  schedulingChartType: Values<typeof SchedulingChartType>,
  forecastDataType?: ForecastType
) => ({
  payload: {
    forecastDataType,
    schedulingChartType,
  },
  type: SchedulingAction.CHANGE_SOURCE_OF_FORECAST,
});

export const loadProductionElementsWorkContribution = () => ({
  type: SchedulingAction.LOAD_PRODUCTION_ELEMENTS_WORK_CONTRIBUTION,
});

export const loadProductionElementsWorkContributionFailed = () => ({
  type: SchedulingAction.LOAD_PRODUCTION_ELEMENTS_WORK_CONTRIBUTION_FAILED,
});

export const calculateProductionElementsWorkContribution = (
  productionElementsWorkContribution: TransformedProductionElementsWorkContribution
) => ({
  type: SchedulingAction.CALCULATE_PRODUCTION_ELEMENTS_WORK_CONTRIBUTION,
  payload: {
    productionElementsWorkContribution,
  },
});

export const loadEstimatedServiceLevel = () => ({
  type: SchedulingAction.LOAD_ESTIMATED_SERVICE_LEVEL,
});

export const loadEstimatedServiceLevelFailed = () => ({
  type: SchedulingAction.LOAD_ESTIMATED_SERVICE_LEVEL_FAILED,
});

export const calculateEstimatedServiceLevel = (
  estimatedServiceLevelPerQueue: Record<string, EstimatedServiceLevelPerQueue>
) => ({
  type: SchedulingAction.CALCULATE_ESTIMATED_SERVICE_LEVEL,
  payload: {
    estimatedServiceLevelPerQueue,
  },
});
